import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import type { Dispatch, StateUpdater } from 'preact/hooks'
import { websApi, type WebViewDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'

interface UseWebLoaderResult {
    web: WebViewDto | null
    setWeb: Dispatch<StateUpdater<WebViewDto | null>>
    loading: boolean
    error: string | null
    /**
     * Reads the web again in place, for a change made elsewhere that the web view is derived from — the Sign Off list
     * reads the landing page's drafts. Keeps what is shown if the read fails, and drops a reply for a web no longer asked for.
     */
    reload: () => Promise<void>
}

/**
 * Loads one web. Either id being blank is treated as "nothing to load yet", not as a failure: a caller
 * that gets its webId from an async fetch of its own renders once before the id exists, and requesting
 * `/webs/` would 404 and raise an error toast for a web nobody asked for.
 *
 * `web` is cleared the moment the ids change, before the request goes out. A caller that switches webs
 * — the asset review overlay, moving between assets — would otherwise keep reading the previous web
 * for the length of the round trip, and keep it forever if the next id is blank or the request fails.
 * That is a wrong answer rather than a missing one: it is indistinguishable from a loaded web, so the
 * preview there would decide a Meeting has event states because the last asset's web did. Callers must
 * treat null as "not known yet" and consult `loading`.
 */
export function useWebLoader(workspaceId: string, webId: string): UseWebLoaderResult {
    const [loading, setLoading] = useState(!!workspaceId && !!webId)
    const [error, setError] = useState<string | null>(null)
    const [web, setWeb] = useState<WebViewDto | null>(null)
    const { showError } = useToast()
    const currentIds = useRef('')
    currentIds.current = `${workspaceId}/${webId}`

    useEffect(() => {
        setWeb(null)
        setError(null)

        if (!workspaceId || !webId) {
            setLoading(false)
            return
        }

        let active = true;
        (async () => {
            setLoading(true)
            const res = await websApi.getById(workspaceId, webId)
            if (!active) return
            if (res.data) setWeb(res.data)
            else {
                const message = res.error || 'Failed to load web details'
                setError(message)
                showError('Load failed', message)
            }
            setLoading(false)
        })()
        return () => { active = false }
    }, [workspaceId, webId])

    const reload = useCallback(async () => {
        if (!workspaceId || !webId) return
        const ids = `${workspaceId}/${webId}`
        const res = await websApi.getById(workspaceId, webId)
        if (res.data && currentIds.current === ids) setWeb(res.data)
    }, [workspaceId, webId])

    return { web, setWeb, loading, error, reload }
}
